import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import type { Ticket } from '@/lib/types'
import { cn, formatScheduled, labelColor } from '@/lib/utils'
import { GitMerge, X, ArrowDown, MessageSquare, CalendarClock } from 'lucide-react'

interface Props {
  /** The card that survives the merge (the older one) — keeps its id,
   *  position, list, schedule… and absorbs the other's content. */
  survivor: Ticket
  /** The card folded into the survivor, then deleted (the newer one). */
  absorbed: Ticket
  onConfirm: () => void
  onCancel: () => void
  /** True while the merge is being applied (buttons disabled). */
  busy?: boolean
}

/** Union of two string arrays, de-duplicated, order-preserving. */
function union(a: string[] | null | undefined, b: string[] | null | undefined): string[] {
  return Array.from(new Set([...(a ?? []), ...(b ?? [])]))
}

/** A compact, read-only preview of a card — title, snippet, message count,
 *  labels and scheduled date. Used for both source cards and the result. */
function MiniCard({
  ticket,
  badge,
  badgeTone = 'neutral',
  mergedLabels,
  mergedMessageCount,
  highlighted = false,
}: {
  ticket: Ticket
  badge?: string
  badgeTone?: 'neutral' | 'keep' | 'drop' | 'accent'
  /** Override the labels shown (for the merged result). */
  mergedLabels?: string[]
  /** Override the message count shown (for the merged result). */
  mergedMessageCount?: number
  highlighted?: boolean
}) {
  const labels = mergedLabels ?? ticket.labels ?? []
  const messageCount = mergedMessageCount ?? ticket.source_message_ids.length
  const scheduled = ticket.scheduled_start
    ? formatScheduled(
        ticket.scheduled_start,
        ticket.scheduled_end,
        ticket.scheduled_all_day,
      )
    : null

  const toneCls =
    badgeTone === 'keep'
      ? 'bg-emerald-100 text-emerald-700'
      : badgeTone === 'drop'
        ? 'bg-[var(--color-danger-bg)] text-[var(--color-danger)]'
        : badgeTone === 'accent'
          ? 'bg-[var(--color-accent)] text-white'
          : 'bg-[var(--color-surface-2)] text-[var(--color-text-dim)]'

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-1.5 rounded-2xl border bg-[var(--color-surface)] px-3 py-2.5 text-left',
        highlighted
          ? 'border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]/40'
          : 'border-[var(--color-border-soft)]',
      )}
    >
      {badge && (
        <span
          className={cn(
            'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            toneCls,
          )}
        >
          {badge}
        </span>
      )}
      <div className="line-clamp-2 text-[13px] font-semibold text-[var(--color-text-strong)]">
        {ticket.title || '—'}
      </div>
      {ticket.description?.trim() && (
        <div className="line-clamp-2 text-[11.5px] leading-snug text-[var(--color-text-dim)]">
          {ticket.description.trim()}
        </div>
      )}
      {labels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {labels.slice(0, 4).map((l) => (
            <span
              key={l}
              className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
              style={{ backgroundColor: labelColor(l) }}
            >
              {l}
            </span>
          ))}
          {labels.length > 4 && (
            <span className="text-[10px] text-[var(--color-text-dim)]">
              +{labels.length - 4}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2.5 text-[11px] text-[var(--color-text-dim)]">
        {messageCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            {messageCount}
          </span>
        )}
        {scheduled && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3 w-3" />
            {scheduled}
          </span>
        )}
      </div>
    </div>
  )
}

export function MergeCardsModal({
  survivor,
  absorbed,
  onConfirm,
  onCancel,
  busy = false,
}: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, busy])

  const mergedLabels = union(survivor.labels, absorbed.labels)
  const mergedMessageCount = union(
    survivor.source_message_ids,
    absorbed.source_message_ids,
  ).length

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-6 pt-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]">
            <GitMerge className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
              {t('merge.title')}
            </h3>
            <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--color-text)]">
              {t('merge.description')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel()}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Visual: the two source cards + merge sign → the result */}
        <div className="px-6 pt-4">
          <div className="flex items-stretch gap-2">
            <div className="flex-1">
              <MiniCard
                ticket={survivor}
                badge={t('merge.keepBadge')}
                badgeTone="keep"
              />
            </div>
            <div className="flex shrink-0 items-center justify-center px-0.5 text-[var(--color-accent)]">
              <GitMerge className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <MiniCard
                ticket={absorbed}
                badge={t('merge.dropBadge')}
                badgeTone="drop"
              />
            </div>
          </div>

          <div className="my-2 flex items-center justify-center text-[var(--color-text-dim)]">
            <ArrowDown className="h-4 w-4" />
          </div>

          <MiniCard
            ticket={survivor}
            badge={t('merge.resultBadge')}
            badgeTone="accent"
            mergedLabels={mergedLabels}
            mergedMessageCount={mergedMessageCount}
            highlighted
          />
        </div>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2 border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/40 px-6 py-3">
          <button
            type="button"
            onClick={() => !busy && onCancel()}
            disabled={busy}
            className="rounded-xl px-3.5 py-2 text-[12.5px] font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)] disabled:opacity-40"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-[var(--shadow-soft)] transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <GitMerge className="h-3.5 w-3.5" />
            {busy ? t('merge.merging') : t('merge.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
